import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  IReEnrollmentCyclesResponse,
  IReEnrollmentCycleResponseById,
  IReEnrollmentState,
  IActivateReEnrollmentCycleResponse,
  IFinalizeReEnrollmentCycleResponse,
} from '../../interface/reEnrollment.interface';
import {
  fetchReEnrollmentCycle,
  fetchReEnrollmentCycles,
  activateReEnrollmentCycle,
  finalizeReEnrollmentCycle,
} from '../action/reEnrollment.action';

const initialState: IReEnrollmentState = {
  cycles: [],
  currentCycle: null,
  pagination: null,
  loading: false,
  isActivating: false,
  error: null,
};

const reEnrollmentSlice = createSlice({
  name: 'reEnrollment',
  initialState,
  reducers: {
    resetCurrentCycle: (state) => {
      state.currentCycle = null;
    },
    resetCycles: (state) => {
      state.cycles = [];
      state.pagination = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all re-enrollment cycles
      .addCase(fetchReEnrollmentCycles.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.cycles = [];
        state.pagination = null;
      })
      .addCase(
        fetchReEnrollmentCycles.fulfilled,
        (state, action: PayloadAction<IReEnrollmentCyclesResponse>) => {
          state.loading = false;
          state.cycles = action.payload.data.cycles;
          state.pagination = {
            page: action.payload.data.page,
            pageSize: action.payload.data.pageSize,
            total: action.payload.data.total,
            totalPages: action.payload.data.totalPages,
          };
        },
      )
      .addCase(fetchReEnrollmentCycles.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_re_enrollment_cycles',
            'Failed to fetch re-enrollment cycles.',
          );
        state.cycles = [];
        state.pagination = null;
      })
      // Fetch single re-enrollment cycle
      .addCase(fetchReEnrollmentCycle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentCycle = null;
      })
      .addCase(
        fetchReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IReEnrollmentCycleResponseById>) => {
          state.loading = false;
          state.currentCycle = action.payload.data.cycle;
          state.error = null;
        },
      )
      .addCase(fetchReEnrollmentCycle.rejected, (state, action) => {
        state.loading = false;
        // action.payload (rejectWithValue) carries the backend envelope;
        // action.error only holds the generic Redux error shape.
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_re_enrollment_cycle',
            'Failed to fetch re-enrollment cycle.',
          );
        state.currentCycle = null;
      })
      // Activate re-enrollment cycle
      //
      // NOTE: action cases must never write `state.error` — that slot means
      // "cycle data failed to load" and drives a full-page error state, so a
      // rejected action used to blank the whole screen behind a 404
      // (CVA-4363). Action failures are surfaced as a toast by the calling
      // page via .unwrap().catch() + showNotification, so there is nothing to
      // record here beyond clearing the in-flight flag.
      .addCase(activateReEnrollmentCycle.pending, (state) => {
        state.isActivating = true;
      })
      .addCase(
        activateReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IActivateReEnrollmentCycleResponse>) => {
          state.isActivating = false;
          // Update cycle in cycles list
          const index = state.cycles.findIndex(
            (cycle) => cycle.id === action.payload.data.cycle.id,
          );
          if (index !== -1) {
            state.cycles[index] = {
              ...state.cycles[index],
              status: action.payload.data.cycle.status,
            };
          }
        },
      )
      .addCase(activateReEnrollmentCycle.rejected, (state) => {
        state.isActivating = false;
      })
      // Finalize re-enrollment cycle — same rule as activate above.
      .addCase(finalizeReEnrollmentCycle.pending, (state) => {
        state.isActivating = true;
      })
      .addCase(
        finalizeReEnrollmentCycle.fulfilled,
        (state, action: PayloadAction<IFinalizeReEnrollmentCycleResponse>) => {
          state.isActivating = false;
          // Update cycle in cycles list
          const index = state.cycles.findIndex(
            (cycle) => cycle.id === action.payload.data.cycle.id,
          );
          if (index !== -1) {
            state.cycles[index] = {
              ...state.cycles[index],
              status: action.payload.data.cycle.status,
            };
          }
          // Optionally update currentCycle
          if (state.currentCycle && state.currentCycle.id === action.payload.data.cycle.id) {
            state.currentCycle = {
              ...state.currentCycle,
              status: action.payload.data.cycle.status,
            };
          }
        },
      )
      .addCase(finalizeReEnrollmentCycle.rejected, (state) => {
        state.isActivating = false;
      });
  },
});

export const { resetCurrentCycle, resetCycles, clearError } = reEnrollmentSlice.actions;

export default reEnrollmentSlice.reducer;
